import React, { useRef } from 'react';
import { FileSpreadsheet, Loader2, X, AlertTriangle, Scissors } from 'lucide-react';

/**
 * One numbered slot a Machanicdesk export is dropped on.
 *
 * <p>Renders only: what the slot is for, what is in it, and what is wrong with that.
 * Reading, checking, ordering and splitting the files happens in {@code slotFile.js},
 * and the decision to upload happens in the tab — this component never speaks HTTP
 * and never decides anything about a file beyond showing it.
 *
 * <p>A slot holds MANY files. The exports usually arrive pre-split — "…-part1of3.csv"
 * to "…-part3of3.csv" — and dropping them one at a time, or three at once, must ADD
 * to the slot. Replacing kept only the last part and lost two thirds of the invoices.
 *
 * <p>Both a drop target and a click target. Dragging nine files onto nine boxes is the
 * fast path; the hidden input is for a trackpad, a phone, or a screen reader.
 *
 * @param {{kind: string, label: string, hint: string}} slot
 * @param {number} number the 1-based slot number printed on the card
 * @param {{files?: Array<{id: string, file: File, rows?: number|null,
 *          parts?: Array<{rows: number}>, needsSplit?: boolean, split?: boolean,
 *          error?: string, preparing?: boolean}>}|undefined} state what is in the slot,
 *          already in upload order
 * @param {(files: File[]) => void} onFiles files were dropped or chosen — to be ADDED
 * @param {(id: string) => void} onRemove one file was taken out
 * @param {() => void} onSplit the person asked for the over-cap files to be split now
 * @param {boolean} disabled while a batch is uploading
 */
export default function ImportSlot({ slot, number, state, onFiles, onRemove, onSplit, disabled }) {
    const inputRef = useRef(null);
    const files = state?.files ?? [];
    const splittable = files.some((f) => f.needsSplit && !f.split && !f.error);
    const splitting = files.some((f) => f.splitting);

    const take = (list) => {
        const chosen = Array.from(list ?? []);
        if (chosen.length && !disabled) {
            onFiles(chosen);
        }
    };

    const hasError = files.some((f) => f.error);
    const tone = hasError
        ? 'border-red-300 bg-red-50'
        : files.length
            ? 'border-emerald-300 bg-emerald-50/60'
            : 'border-dashed border-gray-300 bg-gray-50/60 hover:border-gray-400';

    return (
        <li
            data-testid={`slot-${slot.kind}`}
            className={`relative p-2.5 rounded-lg border ${tone} transition-colors ${
                disabled ? 'opacity-60' : 'cursor-pointer'}`}
            onDragOver={(e) => { e.preventDefault(); }}
            onDrop={(e) => { e.preventDefault(); take(e.dataTransfer?.files); }}
            onClick={() => { if (!disabled) inputRef.current?.click(); }}
        >
            <input
                ref={inputRef}
                type="file"
                accept=".csv,text/csv"
                multiple
                data-testid={`slot-file-${slot.kind}`}
                className="hidden"
                disabled={disabled}
                onChange={(e) => { take(e.target.files); e.target.value = ''; }}
            />

            <p className="text-xs font-semibold text-gray-900 leading-tight">
                <span className="text-gray-400 font-normal">{number}. </span>
                {slot.label}
            </p>

            {files.length === 0 ? (
                <p className="text-[11px] text-gray-500 leading-snug mt-0.5">{slot.hint}</p>
            ) : (
                <ul className="mt-1 space-y-0.5">
                    {files.map((entry) => (
                        <li key={entry.id} className="flex items-start gap-1.5 min-w-0" data-testid={`slot-entry-${slot.kind}`}>
                            {entry.preparing
                                ? <Loader2 size={12} className="animate-spin text-gray-500 mt-0.5 shrink-0" />
                                : entry.error
                                    ? <AlertTriangle size={12} className="text-red-600 mt-0.5 shrink-0" />
                                    : <FileSpreadsheet size={12} className="text-emerald-700 mt-0.5 shrink-0" />}
                            <div className="min-w-0 flex-1">
                                <p className="text-[11px] text-gray-800 truncate" title={entry.file.name}>
                                    {entry.file.name}
                                </p>
                                {entry.error
                                    ? <p className="text-[11px] text-red-700 leading-snug">{entry.error}</p>
                                    : !entry.preparing && (
                                        <p className="text-[11px] text-gray-500">{describeEntry(entry)}</p>
                                    )}
                            </div>
                            {!disabled && (
                                <button
                                    type="button"
                                    aria-label={`Remove ${entry.file.name}`}
                                    onClick={(e) => { e.stopPropagation(); onRemove(entry.id); }}
                                    className="p-0.5 rounded text-gray-400 hover:text-gray-700 hover:bg-white shrink-0"
                                >
                                    <X size={11} />
                                </button>
                            )}
                        </li>
                    ))}
                </ul>
            )}

            {files.length > 1 && (
                <p className="mt-1 text-[11px] font-medium text-gray-700">{describe(files)}</p>
            )}

            {/* The visible choice. Start splits anything still over the caps anyway,
                so this is about seeing what will be sent, not about safety. */}
            {splittable && !disabled && (
                <button
                    type="button"
                    onClick={(e) => { e.stopPropagation(); onSplit(); }}
                    disabled={splitting}
                    className="mt-1.5 flex items-center gap-1 px-2 py-1 rounded-md text-[11px] font-medium
                               bg-amber-600 text-white hover:bg-amber-700 disabled:opacity-50"
                >
                    {splitting ? <Loader2 size={11} className="animate-spin" /> : <Scissors size={11} />}
                    Split into parts
                </button>
            )}
        </li>
    );
}

/** What one file in the slot will send: its rows, its part count, or that it is over the caps. */
export function describeEntry(entry) {
    const rows = entry.rows ?? 0;
    if (entry.split && entry.parts?.length > 1) {
        return `${entry.parts.length} parts · ${rows.toLocaleString()} rows`;
    }
    if (entry.needsSplit) {
        return `${rows.toLocaleString()} rows — over the limit, will be split`;
    }
    return `${rows.toLocaleString()} rows`;
}

/** The slot's total: "3 files · 12,095 rows". */
export function describe(files) {
    const good = files.filter((f) => !f.error);
    const rows = good.reduce((n, f) => n + (f.rows ?? 0), 0);
    return `${good.length} ${good.length === 1 ? 'file' : 'files'} · ${rows.toLocaleString()} rows`;
}
